import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Mail, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [resetUrl, setResetUrl] = useState(""); // demo mode

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data.reset_url) setResetUrl(data.reset_url);
      toast.success("Richiesta inviata");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(resetUrl);
      toast.success("Link copiato negli appunti");
    } catch (_) {
      toast.error("Impossibile copiare");
    }
  };

  return (
    <AuthShell
      step="02 · Ripristino password"
      title="Password dimenticata?"
      subtitle="Inserisci l'email associata all'account: riceverai un link per impostare una nuova password."
    >
      {!sent ? (
        <form onSubmit={submit} data-testid="forgot-form" className="space-y-5">
          <div>
            <Label htmlFor="email" className="font-geist-mono text-[10px] uppercase tracking-[0.18em] text-white/50">
              Email dell'account
            </Label>
            <div className="relative mt-2">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
              <Input
                id="email"
                data-testid="forgot-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="nome@esempio.it"
                className="pl-10 h-11 rounded-sm bg-white/[0.04] border-white/[0.1] text-white placeholder:text-white/25 font-geist focus-visible:ring-[#4D8AFF] focus-visible:border-[#4D8AFF]/50"
              />
            </div>
          </div>

          {error && (
            <div
              data-testid="forgot-error"
              className="font-geist-mono text-[11px] text-red-300 border border-red-400/30 bg-red-500/10 rounded-sm px-3 py-2"
            >
              {error}
            </div>
          )}

          <Button
            data-testid="forgot-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full h-11 rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-geist font-semibold tracking-tight group transition-colors"
          >
            {busy ? (
              <span className="font-geist-mono text-xs uppercase tracking-wider">Invio in corso...</span>
            ) : (
              <span className="flex items-center gap-2">
                Invia link di ripristino
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </span>
            )}
          </Button>
        </form>
      ) : (
        <div data-testid="forgot-success" className="space-y-5">
          <div className="flex items-start gap-3 p-4 border border-emerald-400/25 bg-emerald-400/[0.06] rounded-sm">
            <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-white/80 leading-relaxed">
              Se l'email è registrata, riceverai un link per reimpostare la password.
              Controlla anche la casella spam.
            </div>
          </div>

          {resetUrl && (
            <div className="rounded-sm border border-[#4D8AFF]/30 bg-[#4D8AFF]/[0.06] p-4">
              <div className="font-geist-mono text-[10px] uppercase tracking-wider text-[#4D8AFF] mb-2">
                Demo mode · link di ripristino
              </div>
              <div className="font-geist-mono text-[11px] text-white/70 break-all mb-3" data-testid="forgot-reset-url">
                {resetUrl}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={copyUrl}
                  variant="ghost"
                  size="sm"
                  className="rounded-sm text-[#4D8AFF] hover:text-white hover:bg-white/[0.06] font-geist-mono text-[11px] h-8 px-3"
                  data-testid="forgot-copy-url-btn"
                >
                  <Copy className="h-3 w-3 mr-1" /> copia
                </Button>
                <a
                  href={resetUrl}
                  className="inline-flex items-center h-8 px-3 rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-geist-mono text-[11px] uppercase tracking-wider"
                  data-testid="forgot-open-url-btn"
                >
                  Apri →
                </a>
              </div>
              <div className="mt-3 text-[10px] text-white/40 font-geist-mono leading-relaxed">
                In produzione questo link viene inviato via email; qui è mostrato per test.
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-6 pt-6 border-t border-white/[0.08] flex items-center justify-between font-geist-mono text-[11px] text-white/40">
        <span>Ricordi la password?</span>
        <Link
          to="/login"
          data-testid="back-to-login-link"
          className="text-[#4D8AFF] hover:text-white transition-colors uppercase tracking-wider"
        >
          ← Torna al login
        </Link>
      </div>
    </AuthShell>
  );
}
